import {
  allowedPartsTransitionsFor,
  calculatePartsOrderTotals,
  calculatePriceDeviations,
  defaultPriceDeviationRule,
  hasMaterialDeviation,
  needsPartsReview,
  normalizeCreatePartsOrderInput,
  normalizePartsOrderLines,
  normalizeReceivePartsInput,
  normalizeSavePartsOrderInput,
  normalizePartsOrderSearchInput,
  partsDeliveryStatusFor,
  partsDeliveryAddressLabels,
  supplierProviderLabels,
  workOrderAllowsPartsOrder,
  type CompletePunchoutInput,
  type CreatePartsOrderInput,
  type PartsDeliveryAddress,
  type PartsDeliveryAddressKey,
  type PartsGateway,
  type PartsOrderActionInput,
  type PartsOrderPage,
  type PartsOrderSearchInput,
  type PartsOrderValidation,
  type PartsOverview,
  type PartsStockShortfall,
  type PriceSnapshot,
  type PunchoutSessionHandle,
  type ReceivePartsInput,
  type ResolveReviewInput,
  type SavePartsOrderInput,
  type SaveSupplierConnectionInput,
  type StartPunchoutInput,
  type StockWorkOrderHandle,
  type SubmitPartsOrderInput,
  type SupplierAdapter,
  type SupplierConnectionRecord,
  type SupplierIntegrationEventRecord,
  type SupplierProvider,
  type SupplierPurchaseOrderLineRecord,
  type SupplierPurchaseOrderRecord,
  type SupplierPurchaseOrderStatus,
} from '@forq/parts-data';
import {
  INTERNAL_CUSTOMER_NUMBER,
  type OrderGateway,
  type OrderPositionInput,
  type OrderRecord,
  type SaveOrderInput,
} from '@forq/orders-data';
import type { WarehouseGateway } from '@forq/warehouse-data';
import { createMockSupplierAdapter } from './mockSupplier';

/**
 * Ersatzteilbeschaffung im Demo-Betrieb.
 *
 * Trägt DIESELBEN Regeln wie die Datenbank -- Pflichtvorgang, Mandantengrenze, erlaubte
 * Vorgangsstatus, Statusmaschine, Idempotenz, Preisabweichung. Was hier gilt, gilt auch dort;
 * andernfalls hätte ein Demo-Durchlauf keinen Aussagewert (Anti-Drift-Regel des Hauses).
 *
 * Was hier NICHT geprüft wird: Rechte. Die entscheidet die Datenbank, wie überall in FORQ.
 */

interface PunchoutSessionState {
  readonly id: string;
  readonly token: string;
  readonly provider: SupplierProvider;
  readonly workOrderId: string;
  readonly workOrderNumber: string;
  readonly tenantId: string;
  readonly correlationId: string;
  readonly supplierSessionId: string;
  readonly createdAt: string;
  readonly expiresAt: string;
  usedAt: string | null;
}

interface PartsTenantState {
  connections: SupplierConnectionRecord[];
  sessions: PunchoutSessionState[];
  orders: SupplierPurchaseOrderRecord[];
  events: SupplierIntegrationEventRecord[];
  counter: number;
}

export interface DemoPartsDependencies {
  readonly orders: OrderGateway;
  readonly warehouse: WarehouseGateway;
  /** Adapter je Lieferant. Ohne Angabe steht nur der Übungslieferant bereit. */
  readonly adapters?: Partial<Record<SupplierProvider, SupplierAdapter>>;
  readonly now?: () => Date;
  /** Anzeigename des angemeldeten Benutzers -- im Demo-Betrieb gibt es keine Sitzung. */
  readonly actorName?: string;
}

const DEMO_ACTOR = 'Demo-Benutzer';

function clone<T>(value: T): T {
  return structuredClone(value);
}

function money(value: number): number {
  return Math.round(value * 100) / 100;
}

export class DemoPartsGateway implements PartsGateway {
  private readonly states = new Map<string, PartsTenantState>();
  private readonly adapters: Partial<Record<SupplierProvider, SupplierAdapter>>;
  private readonly orders: OrderGateway;
  private readonly warehouse: WarehouseGateway;
  private readonly now: () => Date;
  private readonly actorName: string;
  /** Bildet die Zeilensperre der Datenbank nach: zwei gleichzeitige Absendeversuche teilen sich einen Lauf. */
  private readonly inFlight = new Map<string, Promise<SupplierPurchaseOrderRecord>>();

  constructor(dependencies: DemoPartsDependencies) {
    this.orders = dependencies.orders;
    this.warehouse = dependencies.warehouse;
    this.adapters = dependencies.adapters ?? { mock: createMockSupplierAdapter() };
    this.now = dependencies.now ?? (() => new Date());
    this.actorName = dependencies.actorName ?? DEMO_ACTOR;
  }

  // =========================================================================
  // Zustand
  // =========================================================================

  private state(tenantId: string): PartsTenantState {
    const existing = this.states.get(tenantId);
    if (existing) return existing;

    const timestamp = this.now().toISOString();
    const connection = (
      provider: SupplierProvider,
      active: boolean,
      punchout: boolean,
      ordering: boolean,
    ): SupplierConnectionRecord => ({
      id: `${tenantId}-parts-${provider}`,
      tenantId,
      provider,
      supplierId: null,
      supplierName: supplierProviderLabels[provider],
      active,
      punchoutEnabled: punchout,
      orderingEnabled: ordering,
      credentialRef: provider === 'mock' ? '' : `FORQ_LIEFERANT_${provider.toUpperCase()}`,
      configVersion: 1,
      priceTolerancePercent: defaultPriceDeviationRule.percent,
      priceToleranceAbsolute: defaultPriceDeviationRule.absolute,
      leadTimeToleranceDays: defaultPriceDeviationRule.leadTimeDays,
      sessionTtlSeconds: 1800,
      lastConnectedAt: null,
      lastError: '',
      createdAt: timestamp,
      updatedAt: timestamp,
    });

    const fresh: PartsTenantState = {
      // TVH und TREX sind angelegt, aber abgeschaltet: solange keine Unterlagen vorliegen,
      // soll die Oberfläche sie zeigen und nicht so tun, als gäbe es sie nicht.
      connections: [
        connection('mock', true, true, true),
        connection('tvh', false, false, false),
        connection('trex', false, false, false),
      ],
      sessions: [],
      orders: [],
      events: [],
      counter: 0,
    };
    this.states.set(tenantId, fresh);
    return fresh;
  }

  private adapterFor(provider: SupplierProvider): SupplierAdapter {
    const adapter = this.adapters[provider];
    if (!adapter) {
      throw new Error(
        `Für ${supplierProviderLabels[provider]} ist in dieser Fassung keine Anbindung eingerichtet.`,
      );
    }
    return adapter;
  }

  private connection(tenantId: string, provider: SupplierProvider): SupplierConnectionRecord {
    const connection = this.state(tenantId).connections.find((entry) => entry.provider === provider);
    if (!connection) {
      throw new Error(`Für ${supplierProviderLabels[provider]} gibt es keine Anbindung.`);
    }
    return connection;
  }

  private record(tenantId: string, purchaseOrderId: string): SupplierPurchaseOrderRecord {
    const found = this.state(tenantId).orders.find((entry) => entry.id === purchaseOrderId);
    if (!found) {
      throw new Error('Diese Ersatzteilbestellung gibt es in diesem Mandanten nicht.');
    }
    return found;
  }

  private replace(tenantId: string, next: SupplierPurchaseOrderRecord): SupplierPurchaseOrderRecord {
    const state = this.state(tenantId);
    state.orders = state.orders.map((entry) => (entry.id === next.id ? next : entry));
    return clone(next);
  }

  private writeEvent(
    tenantId: string,
    entry: Omit<SupplierIntegrationEventRecord, 'id' | 'occurredAt' | 'actorName'> &
      Partial<Pick<SupplierIntegrationEventRecord, 'actorName'>>,
  ): void {
    const state = this.state(tenantId);
    state.events = [
      ...state.events,
      {
        id: `${tenantId}-ereignis-${state.events.length + 1}`,
        occurredAt: this.now().toISOString(),
        actorName: entry.actorName ?? this.actorName,
        ...entry,
      },
    ];
  }

  /** Der eine Ort, an dem ein Statuswechsel stattfindet -- inklusive Prüfung und Protokolleintrag. */
  private transition(
    tenantId: string,
    order: SupplierPurchaseOrderRecord,
    toStatus: SupplierPurchaseOrderStatus,
    eventType: string,
    patch: Partial<SupplierPurchaseOrderRecord> = {},
    outcome: 'ok' | 'error' | 'pending' = 'ok',
    errorKind = '',
    metadata: Readonly<Record<string, unknown>> = {},
  ): SupplierPurchaseOrderRecord {
    if (order.status !== toStatus && !allowedPartsTransitionsFor(order.status).includes(toStatus)) {
      throw new Error(
        `Der Statuswechsel von ${order.status} zu ${toStatus} ist bei einer Ersatzteilbestellung nicht zulässig.`,
      );
    }
    const next: SupplierPurchaseOrderRecord = { ...order, ...patch, status: toStatus };
    this.replace(tenantId, next);
    this.writeEvent(tenantId, {
      purchaseOrderId: order.id,
      punchoutSessionId: order.punchoutSessionId,
      eventType,
      statusBefore: order.status,
      statusAfter: toStatus,
      actorSystem: '',
      correlationId: next.correlationId,
      outcome,
      errorKind,
      metadata,
    });
    return next;
  }

  // =========================================================================
  // Vorgangsprüfung -- die unverhandelbare Regel
  // =========================================================================

  /**
   * Der Vorgang muss es geben, er muss diesem Mandanten gehören, und er muss in einem Status
   * sein, in dem bestellt werden darf. Jede der drei Bedingungen bekommt eine eigene Meldung,
   * weil sie unterschiedliche Ursachen haben.
   */
  private async requireWorkOrder(tenantId: string, workOrderId: string): Promise<OrderRecord> {
    if (!workOrderId?.trim()) {
      throw new Error(
        'Eine Ersatzteilbestellung braucht einen Vorgang. Bitte den Auftrag öffnen und von dort bestellen.',
      );
    }
    let order: OrderRecord;
    try {
      order = await this.orders.getOrder(tenantId, workOrderId);
    } catch {
      throw new Error('Der angegebene Vorgang wurde nicht gefunden.');
    }
    if (order.tenantId !== tenantId) {
      throw new Error('Der angegebene Vorgang gehört zu einem anderen Mandanten.');
    }
    if (!workOrderAllowsPartsOrder(order.status)) {
      throw new Error(
        `Für einen Vorgang im Status „${order.status}" können keine Ersatzteile bestellt werden.`,
      );
    }
    return order;
  }

  private async deliveryAddress(
    tenantId: string,
    key: PartsDeliveryAddressKey,
    workOrder: OrderRecord,
  ): Promise<PartsDeliveryAddress> {
    if (key === 'work-order') {
      const delivery =
        workOrder.addresses.find((entry) => entry.kind === 'delivery') ??
        workOrder.addresses.find((entry) => entry.kind === 'billing');
      if (delivery) {
        return {
          key,
          label: partsDeliveryAddressLabels[key],
          companyName: delivery.companyName ?? '',
          street: delivery.street ?? '',
          postalCode: delivery.postalCode ?? '',
          city: delivery.city ?? '',
          countryCode: delivery.countryCode ?? 'DE',
        };
      }
    }
    // Die Betriebsadresse ist die Vorgabe und der Rückfall. Frei eingetippte Lieferadressen
    // gibt es bewusst nicht -- die Auswahl bestimmt der Server, nicht der Browser.
    return {
      key: 'tenant',
      label: partsDeliveryAddressLabels.tenant,
      companyName: 'Fördertechnik Werl GmbH',
      street: 'Bahnhofstraße 1',
      postalCode: '59457',
      city: 'Werl',
      countryCode: 'DE',
    };
  }

  // =========================================================================
  // Übersicht und Konfiguration
  // =========================================================================

  async getOverview(tenantId: string): Promise<PartsOverview> {
    const orders = this.state(tenantId).orders;
    return {
      draftCount: orders.filter((entry) => ['draft', 'validating', 'validated'].includes(entry.status)).length,
      orderedCount: orders.filter((entry) => ['ordered', 'confirmed'].includes(entry.status)).length,
      reviewCount: orders.filter((entry) => needsPartsReview(entry.status)).length,
      openReceiptCount: orders.filter((entry) =>
        ['ordered', 'confirmed', 'partially-delivered'].includes(entry.status),
      ).length,
    };
  }

  async listConnections(tenantId: string): Promise<readonly SupplierConnectionRecord[]> {
    return clone(this.state(tenantId).connections);
  }

  async saveConnection(input: SaveSupplierConnectionInput): Promise<SupplierConnectionRecord> {
    const state = this.state(input.tenantId);
    const current = this.connection(input.tenantId, input.provider);
    const next: SupplierConnectionRecord = {
      ...current,
      supplierId: input.supplierId ?? current.supplierId,
      active: input.active ?? current.active,
      punchoutEnabled: input.punchoutEnabled ?? current.punchoutEnabled,
      orderingEnabled: input.orderingEnabled ?? current.orderingEnabled,
      credentialRef: input.credentialRef?.trim() ?? current.credentialRef,
      priceTolerancePercent: input.priceTolerancePercent ?? current.priceTolerancePercent,
      priceToleranceAbsolute: input.priceToleranceAbsolute ?? current.priceToleranceAbsolute,
      leadTimeToleranceDays: input.leadTimeToleranceDays ?? current.leadTimeToleranceDays,
      sessionTtlSeconds: input.sessionTtlSeconds ?? current.sessionTtlSeconds,
      configVersion: current.configVersion + 1,
      updatedAt: this.now().toISOString(),
    };
    state.connections = state.connections.map((entry) =>
      entry.provider === next.provider ? next : entry,
    );
    return clone(next);
  }

  // =========================================================================
  // Bestandsabgleich -- der Auslöser
  // =========================================================================

  async checkWorkOrderStock(
    tenantId: string,
    workOrderId: string,
  ): Promise<readonly PartsStockShortfall[]> {
    const workOrder = await this.orders.getOrder(tenantId, workOrderId);
    const articleLines = workOrder.positions.filter(
      (position) => position.kind === 'article' && position.sourceId,
    );
    if (articleLines.length === 0) return [];

    const shortfalls: PartsStockShortfall[] = [];
    for (const position of articleLines) {
      const page = await this.warehouse.searchStock({
        tenantId,
        query: position.sourceNumber || position.description,
        pageSize: 100,
      });
      const rows = page.items.filter((row) => row.itemId === position.sourceId);
      const available = rows.reduce((sum, row) => sum + row.available, 0);
      const missing = Math.round((position.quantity - available) * 1000) / 1000;
      if (missing <= 0) continue;

      const first = rows[0];
      shortfalls.push({
        positionId: position.id,
        positionNumber: position.positionNumber,
        itemId: position.sourceId,
        itemNumber: position.sourceNumber,
        description: position.description,
        unit: position.unit,
        requiredQuantity: position.quantity,
        availableQuantity: available,
        missingQuantity: missing,
        preferredSupplierId: first?.preferredSupplierId ?? null,
        preferredSupplierName: first?.preferredSupplierName ?? '',
        preferredSupplierItemNumber: '',
        manufacturer: '',
        manufacturerPartNumber: '',
      });
    }
    return shortfalls;
  }

  /**
   * Der interne Sammelvorgang der Werkstatt.
   *
   * Verbrauchsmaterial hat keinen Kundenauftrag -- aber einen Vorgang bekommt es trotzdem, sonst
   * wäre die Pflichtzuordnung nur eine Absprache. Der Sammelvorgang läuft auf den eigenen Betrieb
   * (Kundennummer 69999) und trägt die Kosten der Werkstatt.
   */
  async resolveStockWorkOrder(tenantId: string): Promise<StockWorkOrderHandle> {
    const year = this.now().getFullYear();
    const title = `Lager- und Werkstattbedarf ${year}`;

    const existing = await this.orders.searchOrders({
      tenantId,
      query: title,
      bucket: 'all',
      profile: 'internal',
      pageSize: 50,
    });
    const found = existing.items.find(
      (entry) => entry.title === title && workOrderAllowsPartsOrder(entry.status),
    );
    if (found) {
      return { workOrderId: found.id, workOrderNumber: found.number, title };
    }

    const customers = await this.orders.searchCustomerOptions(tenantId, INTERNAL_CUSTOMER_NUMBER);
    const internal = customers.find((entry) => entry.number === INTERNAL_CUSTOMER_NUMBER);

    const input: SaveOrderInput = {
      tenantId,
      profile: 'internal',
      title,
      customerId: internal?.id ?? null,
      notes:
        'Sammelvorgang für Ersatzteil- und Verbrauchsmaterialbestellungen ohne Kundenauftrag. Wird von FORQ angelegt.',
      addresses: internal?.billingAddress ? [internal.billingAddress] : [],
      conditions: {},
      positions: [],
    };
    const created = await this.orders.saveOrder(input);
    return { workOrderId: created.id, workOrderNumber: created.number, title };
  }

  // =========================================================================
  // PunchOut
  // =========================================================================

  async startPunchoutSession(input: StartPunchoutInput): Promise<PunchoutSessionHandle> {
    const workOrder = await this.requireWorkOrder(input.tenantId, input.workOrderId);
    const connection = this.connection(input.tenantId, input.provider);
    if (!connection.active || !connection.punchoutEnabled) {
      throw new Error(
        `Die Artikelsuche bei ${supplierProviderLabels[input.provider]} ist für diesen Mandanten nicht freigeschaltet.`,
      );
    }

    const adapter = this.adapterFor(input.provider);
    const state = this.state(input.tenantId);
    const index = ++state.counter;
    const correlationId = globalThis.crypto.randomUUID();
    const token = globalThis.crypto.randomUUID();
    const created = this.now();
    const expiresAt = new Date(created.getTime() + connection.sessionTtlSeconds * 1000).toISOString();

    const shortfalls = input.shortfallPositionIds?.length
      ? await this.checkWorkOrderStock(input.tenantId, input.workOrderId)
      : [];
    const requested = shortfalls
      .filter((entry) => input.shortfallPositionIds?.includes(entry.positionId))
      .map((entry) => ({
        supplierItemNumber: entry.preferredSupplierItemNumber || entry.itemNumber,
        manufacturerPartNumber: entry.manufacturerPartNumber,
        description: entry.description,
        quantity: entry.missingQuantity,
      }));

    const result = await adapter.createPunchoutSession({
      provider: input.provider,
      tenantId: input.tenantId,
      workOrderId: workOrder.id,
      workOrderNumber: workOrder.number,
      userId: 'demo',
      userName: this.actorName,
      correlationId,
      returnUrl: '/ersatzteile/punchback',
      sessionToken: token,
      expiresAt,
      requestedItems: requested,
    });

    if (result.outcome !== 'ok' || !result.data) {
      this.writeEvent(input.tenantId, {
        purchaseOrderId: null,
        punchoutSessionId: null,
        eventType: 'punchout.start',
        statusBefore: null,
        statusAfter: null,
        actorSystem: '',
        correlationId,
        outcome: 'error',
        errorKind: result.errorKind,
        metadata: result.metadata,
      });
      throw new Error(result.message || 'Die Lieferantensitzung konnte nicht gestartet werden.');
    }

    const session: PunchoutSessionState = {
      id: `${input.tenantId}-sitzung-${index}`,
      token,
      provider: input.provider,
      workOrderId: workOrder.id,
      workOrderNumber: workOrder.number,
      tenantId: input.tenantId,
      correlationId,
      supplierSessionId: result.data.supplierSessionId,
      createdAt: created.toISOString(),
      expiresAt,
      usedAt: null,
    };
    state.sessions = [...state.sessions, session];

    this.writeEvent(input.tenantId, {
      purchaseOrderId: null,
      punchoutSessionId: session.id,
      eventType: 'punchout.start',
      statusBefore: null,
      statusAfter: null,
      actorSystem: '',
      correlationId,
      outcome: 'ok',
      errorKind: '',
      metadata: { provider: input.provider, workOrderNumber: workOrder.number },
    });

    return {
      sessionId: session.id,
      provider: input.provider,
      workOrderId: workOrder.id,
      workOrderNumber: workOrder.number,
      correlationId,
      expiresAt,
      startUrl: result.data.startUrl,
    };
  }

  async completePunchout(input: CompletePunchoutInput): Promise<SupplierPurchaseOrderRecord> {
    const state = this.state(input.tenantId);
    const session = state.sessions.find((entry) => entry.token === input.sessionToken);

    // Reihenfolge der Prüfungen ist Absicht: erst „gibt es die Sitzung überhaupt", dann
    // „gehört sie hierher", dann „ist sie noch gültig", dann „wurde sie schon benutzt".
    if (!session) {
      throw new Error('Diese Lieferantensitzung ist unbekannt.');
    }
    if (session.tenantId !== input.tenantId) {
      throw new Error('Diese Lieferantensitzung gehört zu einem anderen Mandanten.');
    }
    if (session.provider !== 'mock') {
      throw new Error(
        'Nur der Übungslieferant gibt seinen Warenkorb über den Browser zurück. Echte Lieferanten senden ihn an den FORQ-Server.',
      );
    }
    if (Date.parse(session.expiresAt) <= this.now().getTime()) {
      throw new Error('Diese Lieferantensitzung ist abgelaufen. Bitte die Suche neu starten.');
    }
    if (session.usedAt) {
      throw new Error('Diese Lieferantensitzung wurde bereits verwendet.');
    }

    const adapter = this.adapterFor(session.provider);
    const parsed = await adapter.authenticateAndParsePunchback({
      provider: session.provider,
      contentType: 'application/json',
      rawBody: JSON.stringify({ supplierSessionId: session.supplierSessionId, lines: input.lines }),
      headers: { 'x-forq-correlation': session.correlationId },
      sessionToken: input.sessionToken,
    });

    if (parsed.outcome !== 'ok' || !parsed.data) {
      this.writeEvent(input.tenantId, {
        purchaseOrderId: null,
        punchoutSessionId: session.id,
        eventType: 'punchout.return',
        statusBefore: null,
        statusAfter: null,
        actorSystem: '',
        correlationId: session.correlationId,
        outcome: 'error',
        errorKind: parsed.errorKind,
        metadata: parsed.metadata,
      });
      throw new Error(parsed.message || 'Der zurückgegebene Warenkorb wurde nicht angenommen.');
    }

    // Einmalig: erst hier verbraucht, damit ein abgelehnter Warenkorb die Sitzung nicht verbrennt.
    session.usedAt = this.now().toISOString();

    const draft = await this.createDraft({
      tenantId: input.tenantId,
      workOrderId: session.workOrderId,
      provider: session.provider,
      procurementKind: 'work-order',
      punchoutSessionId: session.id,
      shippingMethod: parsed.data.shippingMethod,
      lines: parsed.data.lines.map((line) => ({
        supplierItemNumber: line.supplierItemNumber,
        manufacturer: line.manufacturer,
        manufacturerPartNumber: line.manufacturerPartNumber,
        description: line.description,
        quantity: line.quantity,
        unit: line.unit,
        unitPrice: line.unitPrice,
        currency: line.currency,
        availability: line.availability,
        availabilityText: line.availabilityText,
        leadTimeDays: line.leadTimeDays,
        supplierReference: line.supplierReference,
      })),
    });

    const withShipping = {
      ...this.record(input.tenantId, draft.id),
      shippingCost: money(parsed.data.shippingCost),
    };
    const totals = calculatePartsOrderTotals(withShipping.lines, withShipping.shippingCost);

    this.writeEvent(input.tenantId, {
      purchaseOrderId: draft.id,
      punchoutSessionId: session.id,
      eventType: 'punchout.return',
      statusBefore: null,
      statusAfter: 'draft',
      actorSystem: '',
      correlationId: session.correlationId,
      outcome: 'ok',
      errorKind: '',
      metadata: { lineCount: parsed.data.lines.length },
    });

    return this.replace(input.tenantId, { ...withShipping, ...totals });
  }

  // =========================================================================
  // Entwurf
  // =========================================================================

  async createDraft(raw: CreatePartsOrderInput): Promise<SupplierPurchaseOrderRecord> {
    const input = normalizeCreatePartsOrderInput(raw);
    const workOrder = await this.requireWorkOrder(input.tenantId, input.workOrderId);
    const connection = this.connection(input.tenantId, input.provider);
    if (!connection.active) {
      throw new Error(`Die Anbindung an ${supplierProviderLabels[input.provider]} ist abgeschaltet.`);
    }

    const state = this.state(input.tenantId);
    const index = ++state.counter;
    const id = `${input.tenantId}-bestellung-${index}`;
    const timestamp = this.now().toISOString();
    const year = this.now().getFullYear();

    const lines: SupplierPurchaseOrderLineRecord[] = normalizePartsOrderLines(input.lines).map(
      (line, position) => ({
        id: `${id}-p${position + 1}`,
        purchaseOrderId: id,
        workOrderId: workOrder.id,
        lineNumber: line.lineNumber,
        supplierItemNumber: line.supplierItemNumber,
        manufacturer: line.manufacturer ?? '',
        manufacturerPartNumber: line.manufacturerPartNumber ?? '',
        description: line.description,
        quantity: line.quantity,
        unit: line.unit,
        unitPrice: line.unitPrice,
        lineTotal: line.lineTotal,
        currency: line.currency,
        availability: line.availability,
        availabilityText: line.availabilityText ?? '',
        leadTimeDays: line.leadTimeDays ?? null,
        supplierReference: line.supplierReference ?? '',
        itemId: line.itemId ?? null,
        orderPositionId: line.orderPositionId ?? null,
        receivedQuantity: 0,
        checkedAt: null,
      }),
    );

    const totals = calculatePartsOrderTotals(lines, 0);
    const record: SupplierPurchaseOrderRecord = {
      id,
      tenantId: input.tenantId,
      number: `BES-${year}-${String(index).padStart(5, '0')}`,
      workOrderId: workOrder.id,
      workOrderNumber: workOrder.number,
      workOrderTitle: workOrder.title,
      customerName: workOrder.customerName,
      procurementKind: input.procurementKind,
      provider: input.provider,
      supplierId: connection.supplierId,
      supplierName: connection.supplierName,
      status: 'draft',
      currency: lines[0]?.currency ?? 'EUR',
      deliveryAddress: await this.deliveryAddress(
        input.tenantId,
        input.deliveryAddressKey ?? 'tenant',
        workOrder,
      ),
      shippingMethod: input.shippingMethod ?? '',
      ...totals,
      requestedByName: this.actorName,
      submittedByName: '',
      idempotencyKey: '',
      correlationId: globalThis.crypto.randomUUID(),
      externalOrderNumber: '',
      punchoutSessionId: input.punchoutSessionId ?? null,
      deviations: [],
      deviationsConfirmedAt: null,
      priceCheckedAt: null,
      createdAt: timestamp,
      submittedAt: null,
      lastStatusCheckAt: null,
      failureReason: '',
      reviewReason: '',
      lines,
    };

    state.orders = [...state.orders, record];
    this.writeEvent(input.tenantId, {
      purchaseOrderId: id,
      punchoutSessionId: record.punchoutSessionId,
      eventType: 'order.draft.created',
      statusBefore: null,
      statusAfter: 'draft',
      actorSystem: '',
      correlationId: record.correlationId,
      outcome: 'ok',
      errorKind: '',
      metadata: { workOrderNumber: workOrder.number, lineCount: lines.length },
    });
    return clone(record);
  }

  async saveDraft(raw: SavePartsOrderInput): Promise<SupplierPurchaseOrderRecord> {
    const input = normalizeSavePartsOrderInput(raw);
    const current = this.record(input.tenantId, input.purchaseOrderId);
    if (current.status !== 'draft') {
      throw new Error('Nur ein Entwurf lässt sich ändern.');
    }
    const workOrder = await this.requireWorkOrder(input.tenantId, current.workOrderId);

    const lines: SupplierPurchaseOrderLineRecord[] = normalizePartsOrderLines(input.lines).map(
      (line, position) => {
        const existing = line.lineId
          ? current.lines.find((entry) => entry.id === line.lineId)
          : undefined;
        return {
          id: existing?.id ?? `${current.id}-p${position + 1}-${Date.now()}`,
          purchaseOrderId: current.id,
          workOrderId: current.workOrderId,
          lineNumber: line.lineNumber,
          supplierItemNumber: line.supplierItemNumber,
          manufacturer: line.manufacturer ?? '',
          manufacturerPartNumber: line.manufacturerPartNumber ?? '',
          description: line.description,
          quantity: line.quantity,
          unit: line.unit,
          unitPrice: line.unitPrice,
          lineTotal: line.lineTotal,
          currency: line.currency,
          availability: line.availability,
          availabilityText: line.availabilityText ?? '',
          leadTimeDays: line.leadTimeDays ?? null,
          supplierReference: line.supplierReference ?? '',
          itemId: line.itemId ?? null,
          orderPositionId: line.orderPositionId ?? null,
          receivedQuantity: existing?.receivedQuantity ?? 0,
          checkedAt: existing?.checkedAt ?? null,
        };
      },
    );

    const totals = calculatePartsOrderTotals(lines, current.shippingCost);
    const next: SupplierPurchaseOrderRecord = {
      ...current,
      deliveryAddress: await this.deliveryAddress(
        input.tenantId,
        input.deliveryAddressKey ?? current.deliveryAddress.key,
        workOrder,
      ),
      shippingMethod: input.shippingMethod ?? current.shippingMethod,
      lines,
      ...totals,
      // Eine Änderung am Entwurf entwertet eine vorherige Bestätigung.
      deviations: [],
      deviationsConfirmedAt: null,
    };
    this.writeEvent(input.tenantId, {
      purchaseOrderId: current.id,
      punchoutSessionId: current.punchoutSessionId,
      eventType: 'order.draft.saved',
      statusBefore: current.status,
      statusAfter: current.status,
      actorSystem: '',
      correlationId: current.correlationId,
      outcome: 'ok',
      errorKind: '',
      metadata: { lineCount: lines.length },
    });
    return this.replace(input.tenantId, next);
  }

  // =========================================================================
  // Prüfen
  // =========================================================================

  async validateOrder(input: PartsOrderActionInput): Promise<PartsOrderValidation> {
    const current = this.record(input.tenantId, input.purchaseOrderId);
    await this.requireWorkOrder(input.tenantId, current.workOrderId);
    if (current.status !== 'draft') {
      throw new Error('Geprüft wird ein Entwurf -- diese Bestellung ist bereits weiter.');
    }

    const connection = this.connection(input.tenantId, current.provider);
    const rule = {
      percent: connection.priceTolerancePercent,
      absolute: connection.priceToleranceAbsolute,
      leadTimeDays: connection.leadTimeToleranceDays,
    };

    const validating = this.transition(input.tenantId, current, 'validating', 'order.validate.started');
    const adapter = this.adapterFor(current.provider);
    const checked = await adapter.checkPrices({
      provider: current.provider,
      correlationId: current.correlationId,
      items: current.lines.map((line) => ({
        lineId: line.id,
        supplierItemNumber: line.supplierItemNumber,
        quantity: line.quantity,
      })),
    });

    if (checked.outcome !== 'ok' || !checked.data) {
      // Der Lieferant war nicht erreichbar. Das ist KEIN Grund, nicht zu bestellen -- es ist ein
      // Grund, den Preisstand aus dem Warenkorb weiterzuführen und das sichtbar zu machen.
      const back = this.transition(
        input.tenantId,
        validating,
        'validated',
        'order.validate.unchecked',
        {},
        'pending',
        checked.errorKind,
        checked.metadata,
      );
      return {
        order: clone(back),
        deviations: [],
        requiresConfirmation: false,
        checked: false,
        checkMessage:
          checked.message ||
          'Preis und Verfügbarkeit konnten nicht erneut geprüft werden. Es gilt der Stand aus dem Warenkorb.',
      };
    }

    const before: PriceSnapshot = {
      currency: current.currency,
      shippingCost: current.shippingCost,
      lines: current.lines.map((line) => ({
        lineId: line.id,
        label: `${line.supplierItemNumber} · ${line.description}`,
        quantity: line.quantity,
        unitPrice: line.unitPrice,
        availability: line.availability,
        leadTimeDays: line.leadTimeDays,
      })),
    };
    const checkedById = new Map(checked.data.lines.map((line) => [line.lineId, line]));
    const after: PriceSnapshot = {
      currency: checked.data.currency,
      shippingCost: money(checked.data.shippingCost),
      lines: current.lines
        .filter((line) => checkedById.has(line.id))
        .map((line) => {
          const fresh = checkedById.get(line.id)!;
          return {
            lineId: line.id,
            label: `${line.supplierItemNumber} · ${line.description}`,
            quantity: line.quantity,
            unitPrice: fresh.unitPrice,
            availability: fresh.availability,
            leadTimeDays: fresh.leadTimeDays,
          };
        }),
    };

    const deviations = calculatePriceDeviations(before, after, rule);
    const material = hasMaterialDeviation(deviations);
    const timestamp = this.now().toISOString();

    const lines = current.lines.map((line) => {
      const fresh = checkedById.get(line.id);
      if (!fresh) return { ...line, availability: 'unavailable' as const, checkedAt: timestamp };
      return {
        ...line,
        unitPrice: fresh.unitPrice,
        lineTotal: money(line.quantity * fresh.unitPrice),
        availability: fresh.availability,
        availabilityText: fresh.availabilityText,
        leadTimeDays: fresh.leadTimeDays,
        checkedAt: timestamp,
      };
    });
    const totals = calculatePartsOrderTotals(lines, money(checked.data.shippingCost));

    const patch = {
      lines,
      ...totals,
      shippingMethod: checked.data.shippingMethod || current.shippingMethod,
      deviations,
      priceCheckedAt: timestamp,
      deviationsConfirmedAt: material ? null : timestamp,
    };

    // Wesentliche Abweichung -> zurück in den Entwurf. Der Benutzer sieht alt und neu und
    // entscheidet; ohne seine Bestätigung geht nichts weiter.
    const next = material
      ? this.transition(input.tenantId, validating, 'draft', 'order.validate.deviation', patch, 'pending', 'preisabweichung', {
          deviationCount: deviations.length,
        })
      : this.transition(input.tenantId, validating, 'validated', 'order.validate.ok', patch);

    return {
      order: clone(next),
      deviations,
      requiresConfirmation: material,
      checked: true,
      checkMessage: material
        ? 'Preis oder Verfügbarkeit haben sich wesentlich geändert. Bitte erneut bestätigen.'
        : 'Preis und Verfügbarkeit wurden bestätigt.',
    };
  }

  async confirmDeviations(input: PartsOrderActionInput): Promise<SupplierPurchaseOrderRecord> {
    const current = this.record(input.tenantId, input.purchaseOrderId);
    if (current.status !== 'draft' || current.deviations.length === 0) {
      throw new Error('Es liegen keine Abweichungen vor, die zu bestätigen wären.');
    }
    const timestamp = this.now().toISOString();
    const validating = this.transition(
      input.tenantId,
      current,
      'validating',
      'order.deviation.confirming',
    );
    return clone(
      this.transition(input.tenantId, validating, 'validated', 'order.deviation.confirmed', {
        deviationsConfirmedAt: timestamp,
      }),
    );
  }

  // =========================================================================
  // Absenden
  // =========================================================================

  async submitOrder(input: SubmitPartsOrderInput): Promise<SupplierPurchaseOrderRecord> {
    const key = input.idempotencyKey?.trim();
    if (!key) {
      throw new Error('Zum Absenden fehlt der Idempotenzschlüssel.');
    }

    // Zwei gleichzeitige Versuche teilen sich denselben Lauf -- das ist die Zeilensperre
    // der Datenbank, in der Sprache dieser Fassung.
    const lockKey = `${input.tenantId}:${input.purchaseOrderId}`;
    const running = this.inFlight.get(lockKey);
    if (running) return running;

    const run = this.submitOnce(input, key).finally(() => this.inFlight.delete(lockKey));
    this.inFlight.set(lockKey, run);
    return run;
  }

  private async submitOnce(
    input: SubmitPartsOrderInput,
    key: string,
  ): Promise<SupplierPurchaseOrderRecord> {
    const state = this.state(input.tenantId);
    const current = this.record(input.tenantId, input.purchaseOrderId);

    // Derselbe Schlüssel, dieselbe Bestellung, schon abgesetzt: den vorhandenen Vorgang
    // zurückgeben statt ein zweites Mal zu bestellen.
    if (current.idempotencyKey === key && current.status !== 'validated') {
      return clone(current);
    }
    const foreign = state.orders.find(
      (entry) => entry.idempotencyKey === key && entry.id !== current.id,
    );
    if (foreign) {
      throw new Error('Dieser Absendevorgang gehört zu einer anderen Bestellung.');
    }

    await this.requireWorkOrder(input.tenantId, current.workOrderId);
    const connection = this.connection(input.tenantId, current.provider);
    if (!connection.active || !connection.orderingEnabled) {
      throw new Error(
        `Die elektronische Bestellung bei ${supplierProviderLabels[current.provider]} ist nicht freigeschaltet.`,
      );
    }
    if (current.status !== 'validated') {
      throw new Error('Es wird nur abgesendet, was unmittelbar davor geprüft und bestätigt wurde.');
    }
    if (current.deviations.length > 0 && !current.deviationsConfirmedAt) {
      throw new Error('Die Abweichungen wurden noch nicht bestätigt.');
    }

    const correlationId = globalThis.crypto.randomUUID();
    const submitting = this.transition(input.tenantId, current, 'submitting', 'order.submit.started', {
      idempotencyKey: key,
      correlationId,
    });

    const adapter = this.adapterFor(current.provider);
    const result = await adapter.submitOrder({
      provider: current.provider,
      purchaseOrderNumber: current.number,
      correlationId,
      idempotencyKey: key,
      procurementKind: current.procurementKind,
      workOrderNumber: current.workOrderNumber,
      currency: current.currency,
      deliveryAddress: current.deliveryAddress,
      shippingMethod: current.shippingMethod,
      lines: current.lines.map((line) => ({
        lineId: line.id,
        lineNumber: line.lineNumber,
        supplierItemNumber: line.supplierItemNumber,
        description: line.description,
        quantity: line.quantity,
        unit: line.unit,
        unitPrice: line.unitPrice,
      })),
    });

    const timestamp = this.now().toISOString();

    if (result.outcome === 'ok' && result.data) {
      const shippingCost = money(result.data.shippingCost);
      const totals = calculatePartsOrderTotals(submitting.lines, shippingCost);
      return clone(
        this.transition(input.tenantId, submitting, 'ordered', 'order.submit.ok', {
          externalOrderNumber: result.data.externalOrderNumber,
          submittedAt: result.data.acceptedAt || timestamp,
          submittedByName: this.actorName,
          failureReason: '',
          ...totals,
        }),
      );
    }

    if (result.outcome === 'unknown') {
      return clone(
        this.transition(
          input.tenantId,
          submitting,
          'unknown-external-state',
          'order.submit.unknown',
          {
            failureReason: result.message,
            reviewReason:
              'Zeitüberschreitung beim Absenden. Vor einem neuen Versuch muss geklärt werden, ob der Lieferant die Bestellung hat.',
            lastStatusCheckAt: null,
          },
          'pending',
          result.errorKind,
          result.metadata,
        ),
      );
    }

    return clone(
      this.transition(
        input.tenantId,
        submitting,
        'failed',
        'order.submit.failed',
        { failureReason: result.message },
        'error',
        result.errorKind,
        result.metadata,
      ),
    );
  }

  async cancelOrder(input: PartsOrderActionInput): Promise<SupplierPurchaseOrderRecord> {
    const current = this.record(input.tenantId, input.purchaseOrderId);
    return clone(
      this.transition(input.tenantId, current, 'cancelled', 'order.cancelled', {
        reviewReason: input.reason?.trim() ?? '',
      }),
    );
  }

  // =========================================================================
  // Ansicht
  // =========================================================================

  async getOrder(tenantId: string, purchaseOrderId: string): Promise<SupplierPurchaseOrderRecord> {
    return clone(this.record(tenantId, purchaseOrderId));
  }

  async listOrders(raw: PartsOrderSearchInput): Promise<PartsOrderPage> {
    const input = normalizePartsOrderSearchInput(raw);
    const all = this.state(input.tenantId).orders;
    const needle = input.query.toLowerCase();

    const filtered = all.filter((entry) => {
      if (input.provider !== 'all' && entry.provider !== input.provider) return false;
      if (input.onlyOwn && entry.requestedByName !== this.actorName) return false;
      if (input.bucket === 'review' && !needsPartsReview(entry.status)) return false;
      if (input.bucket === 'done' && !['delivered', 'cancelled'].includes(entry.status)) return false;
      if (
        input.bucket === 'open' &&
        ['delivered', 'cancelled', ...(needsPartsReview(entry.status) ? [entry.status] : [])].includes(
          entry.status,
        )
      ) {
        return false;
      }
      if (!needle) return true;
      return [entry.number, entry.workOrderNumber, entry.supplierName, entry.externalOrderNumber]
        .join(' ')
        .toLowerCase()
        .includes(needle);
    });

    const start = (input.page - 1) * input.pageSize;
    return {
      items: clone(filtered.slice(start, start + input.pageSize)),
      total: filtered.length,
      page: input.page,
      pageSize: input.pageSize,
    };
  }

  async listForWorkOrder(
    tenantId: string,
    workOrderId: string,
  ): Promise<readonly SupplierPurchaseOrderRecord[]> {
    return clone(this.state(tenantId).orders.filter((entry) => entry.workOrderId === workOrderId));
  }

  async listEvents(
    tenantId: string,
    purchaseOrderId: string,
  ): Promise<readonly SupplierIntegrationEventRecord[]> {
    return clone(
      this.state(tenantId).events.filter((entry) => entry.purchaseOrderId === purchaseOrderId),
    );
  }

  // =========================================================================
  // Klärfälle
  // =========================================================================

  async refreshStatus(input: PartsOrderActionInput): Promise<SupplierPurchaseOrderRecord> {
    const current = this.record(input.tenantId, input.purchaseOrderId);
    const adapter = this.adapterFor(current.provider);
    const timestamp = this.now().toISOString();

    if (current.status === 'unknown-external-state') {
      // Erst suchen, NIE blind neu senden.
      if (!adapter.capabilities.findByCorrelationId) {
        return clone(
          this.transition(input.tenantId, current, 'requires-review', 'order.review.manual', {
            reviewReason:
              'Der Lieferant bietet keine Suche über die Korrelations-ID. Der Vorgang muss von Hand geklärt werden.',
            lastStatusCheckAt: timestamp,
          }),
        );
      }
      const found = await adapter.findOrderByCorrelationId(current.correlationId);
      if (found.outcome === 'ok' && found.data) {
        return clone(
          this.transition(input.tenantId, current, 'ordered', 'order.review.found', {
            externalOrderNumber: found.data.externalOrderNumber,
            submittedAt: found.data.acceptedAt,
            submittedByName: current.submittedByName || this.actorName,
            failureReason: '',
            reviewReason: '',
            lastStatusCheckAt: timestamp,
          }),
        );
      }
      // „Nicht gefunden" ist noch kein Beweis dafür, dass nichts angekommen ist -- deshalb
      // Klärfall statt Fehlschlag. Über den Fehlschlag entscheidet ein Mensch.
      return clone(
        this.transition(input.tenantId, current, 'requires-review', 'order.review.not-found', {
          reviewReason: `Beim Lieferanten ist zu dieser Korrelations-ID nichts auffindbar (${found.message}). Bitte im Lieferantenportal prüfen.`,
          lastStatusCheckAt: timestamp,
        }),
      );
    }

    if (!current.externalOrderNumber) {
      throw new Error('Ohne externe Bestellnummer lässt sich kein Status abfragen.');
    }

    const status = await adapter.fetchOrderStatus(current.externalOrderNumber, current.correlationId);
    if (status.outcome !== 'ok' || !status.data) {
      return clone(
        this.transition(
          input.tenantId,
          current,
          'requires-review',
          'order.status.failed',
          {
            reviewReason: status.message,
            lastStatusCheckAt: timestamp,
          },
          'error',
          status.errorKind,
          status.metadata,
        ),
      );
    }

    const mapped: SupplierPurchaseOrderStatus =
      status.data.state === 'shipped'
        ? 'delivered'
        : status.data.state === 'partially-shipped'
          ? 'partially-delivered'
          : status.data.state === 'cancelled'
            ? 'cancelled'
            : status.data.state === 'confirmed'
              ? 'confirmed'
              : current.status;

    if (mapped === current.status) {
      return this.replace(input.tenantId, { ...current, lastStatusCheckAt: timestamp });
    }
    return clone(
      this.transition(input.tenantId, current, mapped, 'order.status.refreshed', {
        lastStatusCheckAt: timestamp,
      }),
    );
  }

  async resolveReview(input: ResolveReviewInput): Promise<SupplierPurchaseOrderRecord> {
    const current = this.record(input.tenantId, input.purchaseOrderId);
    if (!needsPartsReview(current.status)) {
      throw new Error('Diese Bestellung ist kein Klärfall.');
    }
    const reason = input.reason?.trim() ?? '';
    if (reason.length < 3) {
      throw new Error('Bitte festhalten, wie der Fall geklärt wurde.');
    }
    return clone(
      this.transition(input.tenantId, current, input.toStatus, 'order.review.resolved', {
        reviewReason: reason,
        externalOrderNumber: input.externalOrderNumber?.trim() || current.externalOrderNumber,
        ...(input.toStatus === 'draft'
          ? { idempotencyKey: '', deviations: [], deviationsConfirmedAt: null, failureReason: '' }
          : {}),
      }),
    );
  }

  // =========================================================================
  // Wareneingang
  // =========================================================================

  async receiveGoods(raw: ReceivePartsInput): Promise<SupplierPurchaseOrderRecord> {
    const input = normalizeReceivePartsInput(raw);
    const current = this.record(input.tenantId, input.purchaseOrderId);
    if (!['ordered', 'confirmed', 'partially-delivered'].includes(current.status)) {
      throw new Error('Ein Wareneingang ist nur zu einer abgesetzten Bestellung möglich.');
    }

    const byLine = new Map(input.lines.map((line) => [line.lineId, line.receivedQuantity]));
    const lines = current.lines.map((line) => {
      const added = byLine.get(line.id) ?? 0;
      if (added === 0) return line;
      const received = Math.round((line.receivedQuantity + added) * 1000) / 1000;
      if (received > line.quantity) {
        throw new Error(
          `Für ${line.supplierItemNumber} wurde mehr eingegangen als bestellt (${received} von ${line.quantity}).`,
        );
      }
      return { ...line, receivedQuantity: received };
    });

    const toStatus = partsDeliveryStatusFor(lines, current.status);
    if (toStatus === current.status) {
      this.replace(input.tenantId, { ...current, lines });
    } else {
      this.transition(input.tenantId, current, toStatus, 'order.goods.received', { lines });
    }

    // Erst buchen, dann frisch lesen: `bookGoods` kann am Vorgang und am Lager etwas ändern,
    // und der Rückgabewert soll den Stand danach zeigen.
    await this.bookGoods(current, lines, byLine, input.locationId ?? null);
    return clone(this.record(input.tenantId, current.id));
  }

  /**
   * Wohin die Ware geht -- getrennt nach Bestellart.
   *
   * `stock`      -> Lagerzugang über den bestehenden Bewegungslayer.
   * `work-order` -> Artikelposition im Vorgang, damit das Teil abgerechnet werden kann. Ist der
   *                 Artikel bestandsgeführt, wird zusätzlich Zugang UND Abgang gebucht: das Teil
   *                 war kurz im Haus, und das Journal soll das zeigen, ohne den Bestand zu heben.
   */
  private async bookGoods(
    order: SupplierPurchaseOrderRecord,
    lines: readonly SupplierPurchaseOrderLineRecord[],
    received: ReadonlyMap<string, number>,
    locationId: string | null,
  ): Promise<void> {
    const relevant = lines.filter((line) => (received.get(line.id) ?? 0) > 0);
    if (relevant.length === 0) return;

    const locations = await this.warehouse.listLocations(order.tenantId);
    const target = locationId
      ? locations.find((entry) => entry.id === locationId)
      : (locations.find((entry) => entry.defaultLocation) ?? locations[0]);

    if (order.procurementKind === 'stock') {
      if (!target) {
        throw new Error('Für den Lagerzugang gibt es keinen Lagerplatz.');
      }
      for (const line of relevant) {
        if (!line.itemId) continue;
        await this.warehouse.postMovement({
          tenantId: order.tenantId,
          type: 'receipt',
          itemId: line.itemId,
          quantity: received.get(line.id) ?? 0,
          targetLocationId: target.id,
          reason: `Wareneingang ${order.number}`,
          reference: order.number,
        });
      }
      return;
    }

    // Auftragsbezogen: das Teil geht an die Maschine, nicht ins Regal.
    const workOrder = await this.orders.getOrder(order.tenantId, order.workOrderId);
    const additions: OrderPositionInput[] = relevant.map((line) => ({
      kind: 'article',
      sourceId: line.itemId,
      sourceNumber: line.supplierItemNumber,
      description: line.description,
      quantity: received.get(line.id) ?? 0,
      unit: line.unit,
      unitPrice: line.unitPrice,
      purchasePrice: line.unitPrice,
      vatRate: 19,
      internalText: `Aus Ersatzteilbestellung ${order.number} (${order.supplierName})`,
    }));

    const existing: OrderPositionInput[] = workOrder.positions.map((position) => ({
      kind: position.kind,
      sourceId: position.sourceId,
      sourceNumber: position.sourceNumber,
      machineId: position.machineId,
      description: position.description,
      quantity: position.quantity,
      unit: position.unit,
      unitPrice: position.unitPrice,
      purchasePrice: position.purchasePrice,
      discountPercent: position.discountPercent,
      vatRate: position.vatRate,
      internalText: position.internalText,
      customerText: position.customerText,
    }));

    await this.orders.saveOrder({
      tenantId: order.tenantId,
      orderId: workOrder.id,
      profile: workOrder.profile,
      title: workOrder.title,
      customerId: workOrder.customerId,
      contactName: workOrder.contactName,
      contactEmail: workOrder.contactEmail,
      contactPhone: workOrder.contactPhone,
      machineId: workOrder.machineId,
      priority: workOrder.priority,
      assignedTo: workOrder.assignedTo,
      dueAt: workOrder.dueAt,
      notes: workOrder.notes,
      addresses: workOrder.addresses,
      conditions: workOrder.conditions,
      positions: [...existing, ...additions],
    });

    if (target) {
      for (const line of relevant) {
        if (!line.itemId) continue;
        const quantity = received.get(line.id) ?? 0;
        await this.warehouse.postMovement({
          tenantId: order.tenantId,
          type: 'receipt',
          itemId: line.itemId,
          quantity,
          targetLocationId: target.id,
          reason: `Wareneingang ${order.number}`,
          reference: order.number,
        });
        await this.warehouse.postMovement({
          tenantId: order.tenantId,
          type: 'issue',
          itemId: line.itemId,
          quantity,
          sourceLocationId: target.id,
          reason: `Verbrauch im Vorgang ${order.workOrderNumber}`,
          reference: order.workOrderNumber,
        });
      }
    }
  }
}

export function createDemoPartsGateway(dependencies: DemoPartsDependencies): DemoPartsGateway {
  return new DemoPartsGateway(dependencies);
}
